/** Consent/GPC-gated PostHog analytics, replay, and survey wrapper. */
import type posthog from 'posthog-js';
import { detectGpc, hasConsentFor } from './consent';
import { Logger } from './logger';
import {
  createCleanupFeedbackPostHog,
  type CleanupFeedbackSurveyState,
} from './posthog-cleanup-feedback';
import {
  pausePostHogForConsent,
  requirePostHogConsentResume,
  resumePostHogAfterConsent,
} from './posthog-consent-control';
import type { PostHogEventMap, PostHogEventName } from './posthog-events';

export type { CleanupFeedbackSurveyState };

type PostHog = typeof posthog;
export interface PostHogCaptureOptions {
  send_instantly?: boolean
  transport?: 'XHR' | 'fetch' | 'sendBeacon'
}

type PendingCapture = {
  name: PostHogEventName
  properties?: Record<string, unknown>
  options?: PostHogCaptureOptions
};
const PLACEHOLDER_KEY_PATTERNS = [
  /^your-(dev-)?posthog-(project-)?key$/i,
  /^phc_your.*$/i,
];
const DEFAULT_HOST = 'https://us.i.posthog.com';

/** Hostnames served by a local dev server (`next dev`), never a real deployment. */
const LOCAL_DEV_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1', '0.0.0.0']);

/**
 * True when a hostname belongs to a local dev origin. Exceptions thrown against
 * `next dev` share the production project key, so without this gate a
 * half-finished refactor on a developer's laptop is ingested as a fresh
 * production issue in error tracking.
 */
const isLocalDevHostname = (hostname: string | null | undefined): boolean => {
  if (!hostname) {
    return false;
  }
  const normalised = hostname.trim().toLowerCase().replace(/^\[/, '').replace(/\]$/, '');
  return LOCAL_DEV_HOSTNAMES.has(normalised) || normalised.endsWith('.localhost');
};

/** A captured event as seen by a `before_send` hook (posthog-js `CaptureResult`). */
type CapturedEvent = { properties?: Record<string, unknown> } | null;

/** Resolves the origin hostname an event was captured from. */
const eventHostname = (event: CapturedEvent): string | undefined => {
  const currentUrl = event?.properties?.$current_url;
  if (typeof currentUrl === 'string') {
    try {
      return new URL(currentUrl).hostname;
    } catch {
      // Malformed URL - fall back to the live window origin below.
    }
  }
  return typeof window === 'undefined' ? undefined : window.location.hostname;
};

/**
 * `before_send` hook that drops any event captured from a local dev origin, so
 * localhost exceptions and pageviews never reach the production project.
 * Returning `null` tells posthog-js to discard the event.
 *
 * Deliberately NOT installed under mocked E2E: that suite serves a production
 * build from `127.0.0.1` and asserts on the events posthog-js emits, so the
 * filter would silently empty every assertion. See `installBeforeSend`.
 */
const dropLocalDevEvents = (event: CapturedEvent): CapturedEvent => {
  if (event && isLocalDevHostname(eventHostname(event))) {
    return null;
  }
  return event;
};

/**
 * Resolves the `before_send` hook for `ph.init`. Mocked E2E runs a production
 * build on `127.0.0.1`, which the local-dev filter would classify as dev noise
 * and discard, so the hook is omitted there. `NEXT_PUBLIC_E2E_POSTHOG_PLAIN_REQUESTS`
 * is only ever set by `playwright.config.ts`; real deployments never set it.
 */
const installBeforeSend = (e2ePlainRequests: boolean) =>
  (e2ePlainRequests ? undefined : dropLocalDevEvents);

/**
 * Person properties attached to the identified user.
 *
 * `email` is intentionally included: PostHog resolves a person's display name
 * from `person_display_name_properties` (default: `email`, `name`, `username`),
 * so setting it is what makes the Person/User column render the user's email
 * instead of the raw Supabase UUID. This is identity metadata on the person
 * profile - it does NOT relax the "no PII in event properties" rule in
 * `posthog-events.ts`.
 */
export interface PostHogPersonProperties {
  email?: string
  [key: string]: string | number | boolean | undefined
}

let instance: PostHog | null = null;
let loadPromise: Promise<void> | null = null;
let pendingIdentity: {
  distinctId: string
  properties?: Record<string, string | number | boolean>
} | null = null;
const pendingCaptures: PendingCapture[] = [];
const MAX_PENDING_CAPTURES = 20;
const trimString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};
const resolveProjectKey = (): string | undefined => {
  const trimmed = trimString(process.env.NEXT_PUBLIC_POSTHOG_KEY);
  if (!trimmed) {
    return undefined;
  }
  if (PLACEHOLDER_KEY_PATTERNS.some((pattern) => pattern.test(trimmed))) {
    return undefined;
  }
  return trimmed;
};
const resolveHost = (): string => trimString(process.env.NEXT_PUBLIC_POSTHOG_HOST) ?? DEFAULT_HOST;

/** True when PostHog is allowed to load/send under the current consent state. */
export const posthogAllowed = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return hasConsentFor('analytics') && !detectGpc();
};
/** Loads posthog-js once after consent. */
export const loadPostHog = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }
  if (!posthogAllowed()) {
    return;
  }
  if (instance) {
    resumePostHogAfterConsent(instance);
    if (pendingIdentity) {
      instance.identify(pendingIdentity.distinctId, pendingIdentity.properties);
    }
    while (pendingCaptures.length > 0) {
      const event = pendingCaptures.shift()!;
      instance.capture(event.name, event.properties, event.options);
    }
    return;
  }
  const projectKey = resolveProjectKey();
  if (!projectKey) {
    return;
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    try {
      const mod = await import('posthog-js');
      const ph = mod.default;
      if (!posthogAllowed()) {
        pendingCaptures.length = 0;
        loadPromise = null;
        return;
      }
      const e2ePlainRequests = process.env.NEXT_PUBLIC_E2E_POSTHOG_PLAIN_REQUESTS === 'true';
      ph.init(projectKey, {
        api_host: resolveHost(),
        before_send: installBeforeSend(e2ePlainRequests),
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          recordBody: false,
          recordHeaders: false,
          captureCanvas: {
            recordCanvas: false,
          },
        },
        enable_recording_console_log: false,
        disable_surveys_automatic_display: true,
        person_profiles: 'always',
        request_batching: !e2ePlainRequests,
        disable_compression: e2ePlainRequests,
      } as Parameters<PostHog['init']>[1]);
      instance = ph;
      if (!posthogAllowed()) {
        pendingCaptures.length = 0;
        pausePostHogForConsent(ph);
        return;
      }
      requirePostHogConsentResume();
      resumePostHogAfterConsent(ph);
      if (pendingIdentity) {
        ph.identify(pendingIdentity.distinctId, pendingIdentity.properties);
      }
      while (pendingCaptures.length > 0) {
        const event = pendingCaptures.shift()!;
        ph.capture(event.name, event.properties, event.options);
      }
    } catch (error) {
      Logger.warn('PostHog failed to load', {
        error: error instanceof Error ? error.message : String(error),
      });
      loadPromise = null;
    }
  })();

  return loadPromise;
};

/** Stops SDK-owned capture immediately when analytics consent or GPC disallows it. */
export const disablePostHog = (): void => {
  pendingCaptures.length = 0;
  if (instance) {
    pausePostHogForConsent(instance);
  }
};
/** Captures only events and properties allowlisted by PostHogEventMap. */
type CaptureArgs<E extends PostHogEventName> =
  PostHogEventMap[E] extends Record<string, never>
    ? [properties?: undefined, options?: PostHogCaptureOptions]
    : [properties: PostHogEventMap[E], options?: PostHogCaptureOptions];

export const capturePostHogEvent = <E extends PostHogEventName>(
  name: E,
  ...args: CaptureArgs<E>
): void => {
  if (!posthogAllowed()) {
    return;
  }
  try {
    const properties = args[0] as Record<string, unknown> | undefined;
    const options = args[1];
    if (!instance) {
      if (pendingCaptures.length < MAX_PENDING_CAPTURES) {
        pendingCaptures.push({ name, properties, options });
      }
      return;
    }
    instance.capture(name, properties, options);
  } catch {
    // Telemetry must never break the UI.
  }
};

/** Drops undefined/blank entries so we never write empty person properties. */
const normalisePersonProperties = (
  properties?: PostHogPersonProperties,
): Record<string, string | number | boolean> | undefined => {
  if (!properties) {
    return undefined;
  }
  const normalised: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        normalised[key] = trimmed;
      }
      continue;
    }
    normalised[key] = value;
  }
  return Object.keys(normalised).length > 0 ? normalised : undefined;
};

/**
 * Associates the current session with a known user. Pass `{ email }` so the
 * PostHog Person/User column shows the email address rather than the Supabase
 * user id. No-ops when consent is absent or PostHog has not loaded.
 */
export const identifyPostHogUser = (
  distinctId: string,
  properties?: PostHogPersonProperties,
): void => {
  const normalised = normalisePersonProperties(properties);
  pendingIdentity = { distinctId, properties: normalised };
  if (!posthogAllowed()) {
    return;
  }
  try {
    instance?.identify(distinctId, normalised);
  } catch {
    // Telemetry must never break the UI.
  }
};
/** Clears known-user state when the Supabase session ends. */
export const resetPostHogUser = (): void => {
  pendingIdentity = null;
  pendingCaptures.length = 0;
  try {
    instance?.reset();
  } catch {
    // Telemetry must never break sign-out.
  }
};
/** Forwards an exception without risking UI behavior. */
export const capturePostHogException = (error: unknown): void => {
  if (!posthogAllowed()) {
    return;
  }
  try {
    instance?.captureException(error);
  } catch {
    // Telemetry must never break the UI.
  }
};
const cleanupFeedback = createCleanupFeedbackPostHog({
  allowed: posthogAllowed,
  getInstance: () => instance,
  load: loadPostHog,
  trimString,
});

export const {
  getExperimentVariant: getCleanupFeedbackExperimentVariant,
  logDiagnostic: logCleanupFeedbackDiagnostic,
  resolveSurveyId: resolveCleanupFeedbackSurveyId,
  subscribeExperimentVariant: subscribeCleanupFeedbackExperimentVariant,
} = cleanupFeedback;
